import React from 'react';
import { 
  BookOpen, 
  Users, 
  Award, 
  Globe, 
  Target, 
  Heart,
  CheckCircle,
  Star,
  TrendingUp,
  Shield,
  Clock,
  Zap
} from 'lucide-react';

const About = () => {
  const features = [
    {
      icon: BookOpen,
      title: 'Cours de qualité',
      description: 'Des cours créés par des experts dans leur domaine, avec du contenu actualisé et pratique.'
    },
    {
      icon: Users,
      title: 'Communauté active',
      description: 'Rejoignez une communauté de milliers d\'apprenants et d\'instructeurs passionnés.'
    },
    {
      icon: Award,
      title: 'Certificats reconnus',
      description: 'Obtenez des certificats de completion pour valoriser vos nouvelles compétences.'
    },
    {
      icon: Globe,
      title: 'Accessible partout',
      description: 'Apprenez depuis n\'importe où, sur tous vos appareils, à votre rythme.'
    },
    {
      icon: Target,
      title: 'Objectifs clairs',
      description: 'Chaque cours a des objectifs d\'apprentissage précis et mesurables.'
    },
    {
      icon: Heart,
      title: 'Passion pour l\'apprentissage',
      description: 'Nous croyons que l\'apprentissage est la clé du succès et de l\'épanouissement.'
    }
  ];

  const stats = [
    { icon: Users, value: '50,000+', label: 'Étudiants actifs' },
    { icon: BookOpen, value: '500+', label: 'Cours disponibles' },
    { icon: Award, value: '25,000+', label: 'Certificats délivrés' },
    { icon: Star, value: '4.8/5', label: 'Note moyenne' }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Qualité',
      description: 'Nous nous engageons à fournir un contenu de la plus haute qualité, vérifié par des experts.'
    },
    {
      icon: Clock,
      title: 'Flexibilité',
      description: 'Apprenez à votre rythme, quand et où vous voulez, selon votre emploi du temps.'
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'Nous utilisons les dernières technologies pour rendre l\'apprentissage plus efficace et engageant.'
    },
    {
      icon: TrendingUp,
      title: 'Progression',
      description: 'Suivez votre progression en temps réel et atteignez vos objectifs d\'apprentissage.'
    }
  ];

  const team = [
    {
      name: 'Marie Dubois',
      role: 'Fondatrice & CEO',
      description: 'Passionnée d\'éducation et de technologie, Marie a créé Innovactive360° pour démocratiser l\'apprentissage.',
      avatar: 'MD'
    },
    {
      name: 'Thomas Martin',
      role: 'CTO',
      description: 'Expert en développement web et en architecture de plateformes éducatives.',
      avatar: 'TM'
    },
    {
      name: 'Sophie Bernard',
      role: 'Directrice Pédagogique',
      description: 'Spécialiste en sciences de l\'éducation et en conception de parcours d\'apprentissage.',
      avatar: 'SB'
    },
    {
      name: 'Pierre Dubois',
      role: 'Responsable Communauté',
      description: 'Anime et développe notre communauté d\'apprenants et d\'instructeurs.',
      avatar: 'PD'
    }
  ];

  return (
    <div className="min-h-full bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              À propos d'Innovactive360°
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Nous transformons l'apprentissage en ligne avec des cours de qualité, 
              une communauté engagée et des technologies innovantes.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <CheckCircle className="w-5 h-5" />
                <span>Apprentissage personnalisé</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <CheckCircle className="w-5 h-5" />
                <span>Experts reconnus</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <CheckCircle className="w-5 h-5" />
                <span>Communauté active</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-6">
            Notre Mission
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Chez Innovactive360°, nous croyons que l'apprentissage est un droit fondamental. 
            Notre mission est de rendre l'éducation de qualité accessible à tous, partout dans le monde, 
            en utilisant les meilleures technologies et les méthodes pédagogiques les plus efficaces.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <stat.icon className="w-8 h-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-text mb-2">{stat.value}</div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-text mb-6">
              Pourquoi choisir Innovactive360° ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nous offrons une expérience d'apprentissage unique qui combine qualité, 
              flexibilité et innovation pour vous aider à atteindre vos objectifs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-text mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-6">
            Nos Valeurs
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ces valeurs guident tout ce que nous faisons et façonnent notre culture d'entreprise.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <value.icon className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-xl font-semibold text-text mb-3">{value.title}</h3>
              <p className="text-gray-600">{value.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Team Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-text mb-6">
              Notre Équipe
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Rencontrez les personnes passionnées qui font d'Innovactive360° 
              une plateforme d'apprentissage exceptionnelle.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="text-center">
                <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">{member.avatar}</span>
                </div>
                <h3 className="text-xl font-semibold text-text mb-2">{member.name}</h3>
                <p className="text-secondary font-medium mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Prêt à commencer votre parcours d'apprentissage ?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Rejoignez des milliers d'apprenants qui transforment leur carrière 
            avec nos cours de qualité.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/courses"
              className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Découvrir nos cours
            </a>
            <a
              href="/register"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors duration-200"
            >
              S'inscrire gratuitement
            </a>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-6">
            Contactez-nous
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Vous avez des questions ? Notre équipe est là pour vous aider.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-text mb-2">Email</h3>
              <p className="text-gray-600">contact@innovactive360.com</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-text mb-2">Support</h3>
              <p className="text-gray-600">support@innovactive360.com</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-text mb-2">Communauté</h3>
              <p className="text-gray-600">Discord & Forum</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
